import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { AsyncPanel } from '@/components/platform/AsyncPanel';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Checkbox } from '@/components/ui/Checkbox';
import { platformApi } from '@/api/platform';
import { InstitutionPicker } from '@/components/platform/InstitutionPicker';
import { useAsyncData } from '@/hooks';
import {
  IMPORT_BATCH_STATES,
  type ImportBatch,
  type ImportBatchState,
  type ImportProgress,
  type QuarantineReport,
} from '@/types/platform';
import { formatCount, formatDateTime, formatRelative } from '@/utils/format';
import styles from './platform-pages.module.css';

/**
 * PA-16 — bulk institution setup imports, and what they quarantined.
 *
 * THE QUARANTINED ROWS ARE THE SCREEN. A completed import is not interesting; an
 * import that took four thousand rows and held back nine is, and the nine are
 * invisible from a status column. So the quarantine report is the primary content
 * of the detail view, with the row number and column that failed and the values
 * that produced it, because "nine rows failed" is not actionable and "row 412,
 * column date_of_birth, not a date" is.
 *
 * NOTHING HAS TAKEN EFFECT UNTIL A BATCH IS APPROVED, AND THE SCREEN SAYS SO. This
 * page used to call `/platform/imports` — not a route — and modelled a batch as a
 * fire-and-forget job with accepted and rejected counts. Core deliberately does not
 * offer that: an upload is a proposal, staged and inert, and a named administrator
 * has to accept it. `approvedAt` is rendered because it is the only proof anything
 * was written.
 *
 * PENDING_DOMAIN_VALIDATION IS NOT "STILL VALIDATING". Core checks file structure
 * only; whether a class code exists or an email may become a login belongs to
 * EduCore and the identity service. A batch waiting on that verdict has nobody
 * inside CSKY to chase, and an administrator sitting in AWAITING_CORRECTION has
 * work to do right now. Collapsing the two is how a batch sits untouched for a
 * week because everyone assumed somebody else was blocked.
 *
 * There is no upload control here. Files are parsed by the upload service, which
 * holds no cross-tenant authority; this console reports on batches and decides
 * them.
 */

const STATE_TONES: Record<ImportBatchState, string> = {
  RECEIVED: 'mutedTag',
  VALIDATING: 'warnTag',
  STRUCTURE_REJECTED: 'dangerTag',
  AWAITING_CORRECTION: 'warningTag',
  PENDING_DOMAIN_VALIDATION: 'infoTag',
  READY_FOR_APPROVAL: 'okTag',
  APPROVED: 'okTag',
  APPLYING: 'warnTag',
  COMPLETED: 'okTag',
  FAILED: 'dangerTag',
  REJECTED: 'dangerTag',
  CANCELLED: 'mutedTag',
};

/** States a human decision can still move. */
const DECIDABLE: readonly ImportBatchState[] = [
  'RECEIVED',
  'VALIDATING',
  'AWAITING_CORRECTION',
  'PENDING_DOMAIN_VALIDATION',
  'READY_FOR_APPROVAL',
  'STRUCTURE_REJECTED',
];

/** Where a batch can be closed to. Core's own closure states, not an invention. */
const CLOSURE_STATES: readonly ImportBatchState[] = ['REJECTED', 'CANCELLED', 'FAILED'];

const MIN_REASON = 10;

export function ImportsPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [params, setParams] = useSearchParams();
  const institutionId = params.get('institutionId') ?? '';
  const stateParam = params.get('state') ?? '';
  const stateFilter = (IMPORT_BATCH_STATES as readonly string[]).includes(stateParam)
    ? (stateParam as ImportBatchState)
    : undefined;
  const [inspecting, setInspecting] = useState<string | null>(null);

  const fetcher = useCallback(
    () =>
      platformApi.imports.list({
        institutionId: institutionId || undefined,
        state: stateFilter,
      }),
    [institutionId, stateFilter],
  );
  const { state, reload } = useAsyncData<ImportBatch[]>(
    `imports:${institutionId}:${stateFilter ?? 'ALL'}`,
    fetcher,
  );

  const applyFilters = (nextInstitution: string, nextState: string) => {
    const next: Record<string, string> = {};
    if (nextInstitution) next.institutionId = nextInstitution;
    if (nextState) next.state = nextState;
    setParams(next);
  };

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>{t('platform.imports.title')}</h1>
          <p className={styles.pageLead}>{t('platform.imports.lead')}</p>
        </div>
        <button type="button" className={styles.secondaryBtn} onClick={reload}>
          <Icon name="refresh" size={15} />
          {t('platform.dashboard.refresh')}
        </button>
      </header>

      <p className={styles.dataNotice}>
        <Icon name="info" size={15} aria-hidden="true" />
        {t('platform.imports.approvalNotice')}
      </p>

      <div className={styles.filterBar} role="search">
        <div className={styles.filterField}>
          <label className={styles.filterLabel} htmlFor="imports-institution">
            {t('platform.imports.institutionLabel')}
          </label>
          {/* Picking applies immediately — no UUID to paste, no submit to press. */}
          <InstitutionPicker
            id="imports-institution"
            value={institutionId}
            clearable
            onChange={(next) => applyFilters(next, stateFilter ?? '')}
          />
        </div>
        <div className={styles.filterField}>
          <label className={styles.filterLabel} htmlFor="imports-state">
            {t('platform.imports.stateLabel')}
          </label>
          <Select
            id="imports-state"
            value={stateFilter ?? ''}
            onChange={(value) => applyFilters(institutionId, value)}
            placeholder={t('platform.imports.stateAll')}
            options={IMPORT_BATCH_STATES.map((candidate) => ({
              value: candidate,
              label: t(`platform.imports.state.${candidate}`),
            }))}
          />
        </div>
      </div>

      <AsyncPanel
        state={state}
        onRetry={reload}
        isEmpty={(data) => data.length === 0}
        emptyTitle={t('platform.imports.emptyTitle')}
        emptyBody={t('platform.imports.emptyBody')}
      >
        {(data) => (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <caption className={styles.tableCaption}>
                {t('platform.imports.tableCaption', { n: data.length })}
              </caption>
              <thead>
                <tr>
                  <th scope="col">{t('platform.imports.colBatch')}</th>
                  <th scope="col">{t('platform.imports.colTemplate')}</th>
                  <th scope="col">{t('platform.imports.colInstitution')}</th>
                  <th scope="col">{t('platform.imports.colState')}</th>
                  <th scope="col">{t('platform.imports.colRows')}</th>
                  <th scope="col">{t('platform.imports.colApproved')}</th>
                  <th scope="col">
                    <span className={styles.srOnlyText}>{t('platform.imports.colActions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.map((batch) => (
                  <tr key={batch.id}>
                    <th scope="row" className={styles.tableRowHeader}>
                      {batch.batchReference}
                      <span className={styles.tableSub}>{batch.fileName}</span>
                    </th>
                    <td className={styles.mutedCell}>{batch.templateCode}</td>
                    <td className={styles.mutedCell}>{batch.institutionId}</td>
                    <td>
                      <span className={styles[STATE_TONES[batch.state]] ?? styles.mutedTag}>
                        {t(`platform.imports.state.${batch.state}`, { defaultValue: batch.state })}
                      </span>
                      {batch.state === 'PENDING_DOMAIN_VALIDATION' && (
                        <span className={styles.tableSub}>
                          {t('platform.imports.awaitingOwner', {
                            owner: batch.domainValidationOwner ?? '—',
                          })}
                        </span>
                      )}
                    </td>
                    <td>{formatCount(batch.totalRows, locale)}</td>
                    <td className={styles.mutedCell}>
                      {/*
                        Blank is a fact here, not missing data: nothing in this
                        batch has been written to any institution.
                      */}
                      {batch.approvedAt
                        ? `${formatRelative(batch.approvedAt, locale)}${
                            batch.approvedByName ? ` · ${batch.approvedByName}` : ''
                          }`
                        : t('platform.imports.notApproved')}
                    </td>
                    <td>
                      <button
                        type="button"
                        className={styles.linkBtn}
                        onClick={() => setInspecting(batch.id)}
                      >
                        {t('platform.imports.inspect')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AsyncPanel>

      <ImportDetailDialog
        batchId={inspecting}
        onClose={() => setInspecting(null)}
        onChanged={reload}
        locale={locale}
      />
    </div>
  );
}

type BatchDecision = 'APPROVE' | 'CLOSE' | 'WAIVE';

function ImportDetailDialog({
  batchId,
  onClose,
  onChanged,
  locale,
}: {
  batchId: string | null;
  onClose: () => void;
  onChanged: () => void;
  locale: string;
}) {
  const { t } = useTranslation();
  const id = batchId ?? '';
  const enabled = id.length > 0;
  const [decision, setDecision] = useState<BatchDecision | null>(null);

  const batchFetcher = useCallback(() => platformApi.imports.get(id), [id]);
  const { state: batch, reload: reloadBatch } = useAsyncData<ImportBatch>(`import:${id}`, batchFetcher, enabled);

  const progressFetcher = useCallback(() => platformApi.imports.progress(id), [id]);
  const { state: progress, reload: reloadProgress } = useAsyncData<ImportProgress>(
    `import-progress:${id}`,
    progressFetcher,
    enabled,
  );

  const reportFetcher = useCallback(() => platformApi.imports.quarantineReport(id), [id]);
  const { state: report, reload: reloadReport } = useAsyncData<QuarantineReport>(
    `import-quarantine:${id}`,
    reportFetcher,
    enabled,
  );

  const reloadAll = () => {
    reloadBatch();
    reloadProgress();
    reloadReport();
    onChanged();
  };

  if (!batchId) return null;

  return (
    <Modal
      open
      onClose={onClose}
      title={t('platform.imports.detailTitle')}
      description={t('platform.imports.detailLead')}
    >
      <AsyncPanel state={batch} onRetry={reloadBatch}>
        {(data) => (
          <div className={styles.auditDetail}>
            <dl className={styles.detailGrid}>
              <dt>{t('platform.imports.colBatch')}</dt>
              <dd>{data.batchReference}</dd>
              <dt>{t('platform.imports.colTemplate')}</dt>
              <dd>{data.templateCode}</dd>
              <dt>{t('platform.imports.colState')}</dt>
              <dd>{t(`platform.imports.state.${data.state}`, { defaultValue: data.state })}</dd>
              <dt>{t('platform.imports.domainValidation')}</dt>
              <dd>
                {t(`platform.imports.domainStatus.${data.domainValidationStatus}`, {
                  defaultValue: data.domainValidationStatus,
                })}
                {data.domainValidationOwner ? ` · ${data.domainValidationOwner}` : ''}
                {data.domainValidationNote ? ` — ${data.domainValidationNote}` : ''}
              </dd>
              <dt>{t('platform.imports.uploadedBy')}</dt>
              <dd>
                {data.uploadedByName ?? data.uploadedBy}
                {data.createdAt ? ` · ${formatDateTime(data.createdAt, locale)}` : ''}
              </dd>
              <dt>{t('platform.imports.colApproved')}</dt>
              <dd>
                {data.approvedAt
                  ? `${formatDateTime(data.approvedAt, locale)}${
                      data.approvedByName ? ` · ${data.approvedByName}` : ''
                    }`
                  : t('platform.imports.notApproved')}
              </dd>
              {data.closureReason && (
                <>
                  <dt>{t('platform.imports.closureReason')}</dt>
                  <dd>{data.closureReason}</dd>
                </>
              )}
            </dl>

            <section>
              <h3 className={styles.subheading}>{t('platform.imports.progressTitle')}</h3>
              <AsyncPanel state={progress} onRetry={reloadProgress}>
                {(counts) => (
                  <dl className={styles.impactFacts}>
                    <dt>{t('platform.imports.totalRows')}</dt>
                    <dd>{formatCount(counts.totalRows, locale)}</dd>
                    <dt>{t('platform.imports.structureValid')}</dt>
                    <dd>{formatCount(counts.structureValid, locale)}</dd>
                    <dt>{t('platform.imports.domainVerified')}</dt>
                    <dd>{formatCount(counts.domainVerified, locale)}</dd>
                    <dt>{t('platform.imports.quarantined')}</dt>
                    <dd>{formatCount(counts.quarantined, locale)}</dd>
                    <dt>{t('platform.imports.openQuarantine')}</dt>
                    <dd>{formatCount(counts.openQuarantine, locale)}</dd>
                    <dt>{t('platform.imports.waivedRows')}</dt>
                    <dd>{formatCount(counts.waivedRows, locale)}</dd>
                    <dt>{t('platform.imports.imported')}</dt>
                    <dd>{formatCount(counts.imported, locale)}</dd>
                  </dl>
                )}
              </AsyncPanel>
            </section>

            <section>
              <h3 className={styles.subheading}>{t('platform.imports.errorsTitle')}</h3>
              <AsyncPanel
                state={report}
                onRetry={reloadReport}
                isEmpty={(value) => value.entries.length === 0}
                emptyTitle={t('platform.imports.noErrors')}
              >
                {(value) => (
                  <>
                    <p className={styles.rowMetaLine}>
                      <a
                        className={styles.linkBtn}
                        href={platformApi.imports.quarantineReportCsvPath(data.id)}
                        rel="noreferrer"
                      >
                        <Icon name="external" size={15} />
                        {t('platform.imports.downloadCsv')}
                      </a>
                    </p>
                    <div className={styles.tableWrap}>
                      <table className={styles.table}>
                        <caption className={styles.tableCaption}>
                          {t('platform.imports.errorsCaption', { n: value.entries.length })}
                        </caption>
                        <thead>
                          <tr>
                            <th scope="col">{t('platform.imports.colRow')}</th>
                            <th scope="col">{t('platform.imports.colColumn')}</th>
                            <th scope="col">{t('platform.imports.colProblem')}</th>
                            <th scope="col">{t('platform.imports.colRaisedBy')}</th>
                            <th scope="col">{t('platform.imports.colResolution')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {value.entries.map((entry) => (
                            <tr key={`${entry.rowNumber}-${entry.column ?? entry.reasonCode}`}>
                              <th scope="row" className={styles.tableRowHeader}>
                                {entry.rowNumber}
                              </th>
                              <td className={styles.mutedCell}>{entry.column ?? '—'}</td>
                              <td>
                                {entry.detail}
                                <span className={styles.tableSub}>{entry.reasonCode}</span>
                              </td>
                              <td className={styles.mutedCell}>{entry.raisedBy}</td>
                              <td>
                                <span
                                  className={
                                    entry.resolution === 'OPEN'
                                      ? styles.dangerTag
                                      : entry.resolution === 'WAIVED'
                                        ? styles.warnTag
                                        : styles.okTag
                                  }
                                >
                                  {t(`platform.imports.resolution.${entry.resolution}`, {
                                    defaultValue: entry.resolution,
                                  })}
                                </span>
                                {entry.resolutionReason && (
                                  <span className={styles.tableSub}>{entry.resolutionReason}</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </AsyncPanel>
            </section>

            {DECIDABLE.includes(data.state) && (
              <div className={styles.transitionRow}>
                <button
                  type="button"
                  className={styles.transitionBtn}
                  onClick={() => setDecision('APPROVE')}
                >
                  {t('platform.imports.approve')}
                </button>
                <button type="button" className={styles.transitionBtn} onClick={() => setDecision('WAIVE')}>
                  {t('platform.imports.waive')}
                </button>
                <button
                  type="button"
                  className={styles.transitionBtnDanger}
                  onClick={() => setDecision('CLOSE')}
                >
                  {t('platform.imports.close')}
                </button>
              </div>
            )}

            <BatchDecisionDialog
              decision={decision}
              batch={data}
              openQuarantine={progress.status === 'ready' ? progress.data.openQuarantine : null}
              waivedRows={progress.status === 'ready' ? progress.data.waivedRows : null}
              onClose={() => setDecision(null)}
              onSaved={() => {
                setDecision(null);
                reloadAll();
              }}
            />
          </div>
        )}
      </AsyncPanel>

      <div className="modal-actions">
        <button type="button" className="modal-button" onClick={onClose}>
          {t('common.close')}
        </button>
      </div>
    </Modal>
  );
}

function BatchDecisionDialog({
  decision,
  batch,
  openQuarantine,
  waivedRows,
  onClose,
  onSaved,
}: {
  decision: BatchDecision | null;
  batch: ImportBatch;
  openQuarantine: number | null;
  waivedRows: number | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [note, setNote] = useState('');
  const [acknowledgeWaived, setAcknowledgeWaived] = useState(false);
  const [closureState, setClosureState] = useState<ImportBatchState>('REJECTED');
  const [rows, setRows] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!decision) return;
    setNote('');
    setAcknowledgeWaived(false);
    setClosureState('REJECTED');
    setRows('');
    setError(null);
  }, [decision]);

  if (!decision) return null;

  const rowNumbers = rows
    .split(',')
    .map((entry) => Number(entry.trim()))
    .filter((entry) => Number.isInteger(entry) && entry > 0);

  const needsWaiverAcknowledgement = decision === 'APPROVE' && (waivedRows ?? 0) > 0;

  const valid =
    decision === 'APPROVE'
      ? (!needsWaiverAcknowledgement || acknowledgeWaived)
      : decision === 'WAIVE'
        ? rowNumbers.length > 0 && note.trim().length >= MIN_REASON
        : note.trim().length >= MIN_REASON;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      if (decision === 'APPROVE') {
        await platformApi.imports.approve(batch.id, {
          note: note.trim() || undefined,
          acknowledgeWaivedRows: needsWaiverAcknowledgement ? true : undefined,
        });
      } else if (decision === 'WAIVE') {
        await platformApi.imports.waive(batch.id, rowNumbers, note.trim());
      } else {
        await platformApi.imports.close(batch.id, { state: closureState, reason: note.trim() });
      }
      onSaved();
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={t(`platform.imports.decision.${decision}.title`, { batch: batch.batchReference })}
      description={t(`platform.imports.decision.${decision}.lead`)}
      closeOnBackdropClick={false}
    >
      <form className={styles.dialogForm} onSubmit={submit} noValidate>
        {decision === 'APPROVE' && (
          <>
            {/*
              Approval is the only step that lets anything take effect, and the
              dialog says so rather than treating it as a formality.
            */}
            <div className={styles.warningNotice} role="note">
              <Icon name="warning" size={16} aria-hidden="true" />
              <div>
                <p className={styles.warningTitle}>{t('platform.imports.approveTitle')}</p>
                <p className={styles.warningBody}>{t('platform.imports.approveBody')}</p>
              </div>
            </div>

            {openQuarantine !== null && openQuarantine > 0 && (
              <p className={styles.formError} role="alert">
                {t('platform.imports.openQuarantineBlocks', { n: openQuarantine })}
              </p>
            )}

            {needsWaiverAcknowledgement && (
              <Checkbox
                label={t('platform.imports.acknowledgeWaived', { n: waivedRows ?? 0 })}
                checked={acknowledgeWaived}
                onChange={(event) => setAcknowledgeWaived(event.target.checked)}
              />
            )}

            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('platform.imports.approvalNote')}</span>
              <textarea
                className={styles.textarea}
                rows={3}
                value={note}
                onChange={(event) => setNote(event.target.value)}
              />
            </label>
          </>
        )}

        {decision === 'WAIVE' && (
          <>
            <div className={styles.warningNotice} role="note">
              <Icon name="warning" size={16} aria-hidden="true" />
              <div>
                <p className={styles.warningTitle}>{t('platform.imports.waiveTitle')}</p>
                <p className={styles.warningBody}>{t('platform.imports.waiveBody')}</p>
              </div>
            </div>

            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('platform.imports.waiveRowsLabel')}</span>
              <input
                className={styles.input}
                value={rows}
                required
                inputMode="numeric"
                onChange={(event) => setRows(event.target.value)}
              />
              <span className={styles.fieldHint}>{t('platform.imports.waiveRowsHint')}</span>
            </label>

            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('platform.confirm.reasonLabel')}</span>
              <textarea
                className={styles.textarea}
                rows={3}
                value={note}
                required
                aria-invalid={note.length > 0 && note.trim().length < MIN_REASON}
                onChange={(event) => setNote(event.target.value)}
              />
            </label>
          </>
        )}

        {decision === 'CLOSE' && (
          <>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('platform.imports.closureStateLabel')}</span>
              <Select
                value={closureState}
                onChange={(value) => setClosureState(value as ImportBatchState)}
                options={CLOSURE_STATES.map((candidate) => ({
                  value: candidate,
                  label: t(`platform.imports.state.${candidate}`),
                }))}
              />
              {/*
                REJECTED and FAILED are not the same fact: one was a decision, the
                other was a breakage, and an operator reading this list next month
                needs to know which.
              */}
              <span className={styles.fieldHint}>{t('platform.imports.closureStateHint')}</span>
            </label>

            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('platform.confirm.reasonLabel')}</span>
              <textarea
                className={styles.textarea}
                rows={3}
                value={note}
                required
                aria-invalid={note.length > 0 && note.trim().length < MIN_REASON}
                onChange={(event) => setNote(event.target.value)}
              />
            </label>
          </>
        )}

        {error && (
          <p className={styles.formError} role="alert">
            {error}
          </p>
        )}

        <div className="modal-actions">
          <button
            type="button"
            className="modal-button modal-button--secondary"
            onClick={onClose}
            disabled={submitting}
          >
            {t('common.cancel')}
          </button>
          <button type="submit" className="modal-button" disabled={!valid || submitting}>
            {submitting ? t('platform.confirm.submitting') : t(`platform.imports.decision.${decision}.confirm`)}
          </button>
        </div>
      </form>
    </Modal>
  );
}
